import SplitController from "./split_controller"

// The plan page (spec 2026-10-15): the onboarding packet's controls, now on
// the plan itself. Inherits the amount field and slider, the preset chips,
// the percent inputs with their bars, the adds-up-to-100 gate and the
// could-become projection from the split controller, and adds what a saved
// plan needs: knowing what changed, and the one Save that reads "Save and
// sign" when the amount moved.
//
// Stocks and the mix alone post the form and are written at once. A changed
// amount needs one signature, since the cap in the wallet is the amount:
// Save then stages the change (amount, mix and stocks together) over JSON
// and hands the same tap to the wallet signer in the right column. Without
// JavaScript the form posts the same way and the page comes back with the
// signer rendered by the server.
//
// Sits on the grid that holds both packets, so the form on the left and the
// buttons on the right are all its targets.
export default class extends SplitController {
  static targets = [...SplitController.targets, "saveLabel", "signNote", "error", "signer"]
  static values = {
    ...SplitController.values,
    amountWas: Number,   // the amount the plan runs on now
    stocksWas: Array,    // the stocks it buys now
    splitWas: Object,    // its mix now
    stageUrl: String,
    locked: Boolean      // the amount cannot change (paused, or a change staged)
  }

  // --- what changed ---------------------------------------------------------

  get amountChanged() {
    const amount = this.amount()
    return !this.lockedValue && amount > 0 && amount !== this.amountWasValue
  }

  get stocksChanged() {
    const now = this.stocks()
    const was = [...this.stocksWasValue].sort()
    return now.length !== was.length || now.some((key, index) => key !== was[index])
  }

  get splitChanged() {
    if (!this.hasGoldTarget && !this.hasStocksTarget && !this.hasCryptoTarget) return false
    const now = this.split()
    const was = this.splitWasValue
    return ["gold", "stocks", "crypto"].some((bag) => now[bag] !== Number(was[bag] || 0))
  }

  // The split controller gates Save on the mix adding up and the Stocks
  // basket having a stock to buy; this adds "and something changed".
  refresh() {
    super.refresh()
    const amountChanged = this.amountChanged
    const changed = amountChanged || this.stocksChanged || this.splitChanged
    this.saveTargets.forEach((button) => { button.disabled = button.disabled || !changed })
    this.saveLabelTargets.forEach((label) => { label.textContent = amountChanged ? "Save and sign" : "Save" })
    this.signNoteTargets.forEach((note) => { note.hidden = !amountChanged })
  }

  // --- Save -----------------------------------------------------------------

  async submit(event) {
    if (!this.amountChanged) return // stocks or mix alone: the form posts as it is

    event.preventDefault()
    this.showError(null)
    this.saveTargets.forEach((button) => { button.disabled = true })

    let data = {}
    try {
      const response = await fetch(this.stageUrlValue, {
        method: "POST",
        headers: {
          "Accept": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || "",
          "X-Requested-With": "XMLHttpRequest"
        },
        body: new FormData(event.target),
        credentials: "same-origin"
      })
      data = await response.json().catch(() => ({}))
      if (!response.ok) throw new Error(data.error || "We couldn't save that. Please try again.")
    } catch (error) {
      this.showError(error.message)
      this.refresh()
      return
    }

    // Staged. The amount is now locked until it is signed or dropped; the
    // wallet opens on this same tap.
    this.lockedValue = true
    if (this.hasAmountTarget) this.amountTarget.disabled = true
    if (this.hasAmountSliderTarget) this.amountSliderTarget.disabled = true
    this.saveTargets.forEach((button) => { button.hidden = true })
    this.signNoteTargets.forEach((note) => { note.hidden = true })
    this.signerTargets.forEach((signer) => { signer.hidden = false })

    const element = this.hasSignerTarget && this.signerTarget.querySelector('[data-controller~="approval"]')
    const approval = element && this.application.getControllerForElementAndIdentifier(element, "approval")
    const button = element && element.querySelector('[data-approval-target="confirm"]')
    if (approval && button) await approval.sign({ currentTarget: button })
  }

  showError(message) {
    this.errorTargets.forEach((line) => {
      line.hidden = !message
      line.textContent = message || ""
    })
  }
}
